import { createContext, useContext, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Map the current route to a stable "area" key (one per page/live-room).
export function areaFromPath(pathname) {
  const p = (pathname || "/").toLowerCase().replace(/\/+$/, "") || "/";
  if (p === "/") return "home";
  if (p.startsWith("/arena/battle")) return "arena:battle";
  if (p.startsWith("/arena/watch")) return "arena:watch";
  if (p.startsWith("/arena")) return "arena";
  if (p.startsWith("/race/watch")) return "race:watch";
  if (/^\/race\/[^/]+/.test(p)) return "race:track";
  if (p.startsWith("/race")) return "race";
  if (/^\/cavern\/[^/]+/.test(p)) return "cavern:room";
  if (p.startsWith("/cavern")) return "cavern";
  if (p.startsWith("/trades")) return "trades";
  if (p.startsWith("/store")) return "store";
  if (p.startsWith("/explore")) return "explore";
  if (p.startsWith("/collection")) return "collection";
  if (p.startsWith("/talentshow/")) return "talent:room";
  if (p.startsWith("/talentshow")) return "talentshow";
  if (p.startsWith("/upgrade")) return "upgrade";
  if (p.startsWith("/ascend")) return "ascend";
  if (p.startsWith("/path")) return "path";
  if (p.startsWith("/card/")) return "card";
  if (p.startsWith("/redeem")) return "redeem";
  if (p.startsWith("/requests")) return "requests";
  if (p.startsWith("/profile")) return "profile";
  if (p.startsWith("/help")) return "help";
  if (p.startsWith("/admin")) return "admin";
  return "home";
}

export const AREA_LABEL = {
  home: "the Lobby", arena: "the Arena", "arena:battle": "an Arena battle", "arena:watch": "an Arena match",
  cavern: "the Cavern", "cavern:room": "a Cavern raid", race: "the Race lobby", "race:track": "a live Race",
  "race:watch": "a Race", trades: "the Trade hall", store: "the Store", explore: "the Gallery",
  collection: "their Collection", talentshow: "the Talent Show", "talent:room": "a Talent stage",
  upgrade: "the Forge", ascend: "the Ascension altar", path: "the Path tree", card: "a card",
  redeem: "Redeem", requests: "Requests", profile: "Profile", help: "the Help guide", admin: "Admin",
};

// Areas that have a matching live chat channel — used to tie presence into chat.
export function areaToChannel(area) {
  if (area.startsWith("arena")) return "arena";
  if (area.startsWith("cavern")) return "cavern";
  if (area.startsWith("race")) return "race";
  if (area === "trades") return "trade";
  return null;
}

const PresenceContext = createContext({ area: "home", count: 0, users: [], label: "the Lobby" });
export const usePresence = () => useContext(PresenceContext);

export function PresenceProvider({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const area = useMemo(() => areaFromPath(location.pathname), [location.pathname]);
  const areaRef = useRef(area);
  areaRef.current = area;

  // Heartbeat the current area immediately on change, then on an interval.
  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    const beat = () => { if (alive) api.post("/presence/heartbeat", { area: areaRef.current }).catch(() => {}); };
    beat();
    const id = setInterval(beat, 15000);
    const onLeave = () => { try { navigator.sendBeacon; } catch { /* noop */ } };
    window.addEventListener("beforeunload", onLeave);
    return () => { alive = false; clearInterval(id); window.removeEventListener("beforeunload", onLeave); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, area]);

  const { data } = useQuery({
    queryKey: ["presence", area],
    queryFn: async () => (await api.get(`/presence/${encodeURIComponent(area)}`)).data,
    refetchInterval: 12000,
    enabled: !!user,
  });

  const value = useMemo(() => ({
    area,
    label: AREA_LABEL[area] || "this page",
    count: data?.count || 0,
    users: data?.users || [],
  }), [area, data]);

  return <PresenceContext.Provider value={value}>{children}</PresenceContext.Provider>;
}
